import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Outlet, createRootRoute, Link, useLocation, useNavigate } from '@tanstack/react-router';
import {
  Building2,
  Folder,
  Home,
  LogOut,
  MessageSquare,
  PlayCircle,
  ShieldCheck,
  Sparkles,
  Users,
  UsersRound,
} from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { useAuthMe } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { ThemeSwitcher } from '@/bench/Theme';

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** When true, only show this nav item to admins. */
  adminOnly?: boolean;
};

const NAV: NavItem[] = [
  { to: '/', label: 'Home', icon: Home, adminOnly: true },
  { to: '/onboarding', label: 'Onboard', icon: Building2, adminOnly: true },
  { to: '/knowledge', label: 'Registers', icon: Folder, adminOnly: true },
  { to: '/personas', label: 'Personas', icon: UsersRound, adminOnly: true },
  { to: '/prompts', label: 'Prompts', icon: Sparkles },
  { to: '/runs', label: 'Runs', icon: PlayCircle },
  { to: '/reviews', label: 'Reviews', icon: MessageSquare, adminOnly: true },
  { to: '/users', label: 'Users', icon: Users, adminOnly: true },
];

const PUBLIC_ROUTES = new Set(['/login', '/register']);

export const Route = createRootRoute({
  component: function RootLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { user, isAdmin } = useAuthMe();
    const isPublic = PUBLIC_ROUTES.has(location.pathname);

    const logoutMut = useMutation({
      mutationFn: () => api.authLogout(),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['auth', 'me'] });
        toast.success('Signed out');
        navigate({ to: '/login' });
      },
      onError: (e) => toast.error(String(e)),
    });

    // Public routes (login / register) get a minimal chrome — no nav,
    // no logout. Everything else gets the full app shell.
    if (isPublic) {
      return (
        <div className="min-h-screen bg-background">
          <main className="mx-auto w-full max-w-screen-xl px-6 py-8">
            <Outlet />
          </main>
        </div>
      );
    }

    if (location.pathname === '/') return <Outlet />;

    const displayEmail = user?.email ?? '';
    const displayRole = user?.role;
    const isAnonymous = displayEmail === 'anonymous';

    return (
      <div className="min-h-screen flex flex-col">
        <header className="border-b border-border bg-card">
          <div className="mx-auto flex max-w-screen-xl flex-wrap items-center gap-4 px-6 py-3">
            <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="ClearGrid Scribe home">
              <img src="/cleargrid-lockup-light.svg" alt="ClearGrid" className="cleargrid-logo-light" width="120.615" height="32" />
              <img src="/cleargrid-lockup-dark.svg" alt="ClearGrid" className="cleargrid-logo-dark" width="120.615" height="32" />
              <span className="text-base font-semibold text-foreground">Scribe</span>
            </Link>
            <nav className="flex flex-wrap items-center gap-1">
              {NAV
                .filter((n) => n.to !== '/')
                .filter((n) => isAdmin || !n.adminOnly)
                .map(({ to, label, icon: Icon }) => (
                  <Link
                    key={to}
                    to={to}
                    className={cn(
                      'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
                      '[&.active]:bg-accent [&.active]:text-foreground',
                    )}
                    activeProps={{ className: 'active' }}
                  >
                    <Icon className="size-4" />
                    {label}
                  </Link>
                ))}
            </nav>

            <ThemeSwitcher />
            {user && (
              <div className="ml-auto flex items-center gap-2 text-xs">
                {!isAnonymous && (
                  <div className="flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2.5 py-1">
                    {isAdmin && (
                      <span
                        title="Admin — sees prompts owned by every user"
                        className="inline-flex items-center gap-1 rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary"
                      >
                        <ShieldCheck className="size-3" />
                        admin
                      </span>
                    )}
                    <span className="font-medium text-foreground">{displayEmail}</span>
                  </div>
                )}
                {!isAnonymous && (
                  <button
                    onClick={() => logoutMut.mutate()}
                    disabled={logoutMut.isPending}
                    title="Sign out"
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                  >
                    <LogOut className="size-3.5" />
                    Sign out
                  </button>
                )}
                {isAnonymous && (
                  <Link
                    to="/login"
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    Sign in
                  </Link>
                )}
                {displayRole === 'user' && (
                  <span
                    className="text-[10px] uppercase tracking-wide text-muted-foreground"
                    title="Regular user — only sees prompts you own"
                  >
                    {/* keep role hint in tooltip; no extra chip */}
                  </span>
                )}
              </div>
            )}
          </div>
        </header>
        <main className="mx-auto w-full max-w-screen-xl flex-1 px-6 py-8">
          <Outlet />
        </main>
      </div>
    );
  },
});
